import orderBy from 'lodash/orderBy';
import type { IMatch } from '@/interface/IMatch';
import type {
    IMarketGroup, IOdds, IPriceOption, MainMarketOdds, MainMarketOddsResult, MatchDetailOddsResult, NewBetTypeColumn, NewBetTypeOddsResult, OddsColumn, OtherMarketOddsResult,
} from '@/interface/IOdds';
import type { IOutrightTournament } from '@/interface/IOutright';
import { BetTypeGroup, MarketType, SportType } from '@/interface/enum';
import { _2dArrayPadRight, getLastElement, padRight } from '@/libraries/array';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import type { MainMarketConfig } from '@/libraries/marketTypeDisplayRule';
import {
    NUMBER_OF_BET_TYPE_PER_ROW,
    NUMBER_OF_MAX_DISPLAYED_ODDS_BEFORE_EXPAND,
    getMainMarketConfig,
} from '@/libraries/marketTypeDisplayRule';
import { keyLookup } from '@/libraries/utils/utils';

const TOP_TWO_ROW = 2;

export function isFormatPoint(marketType: number) {
    return (marketType === MarketType.FH_Handicap
        || marketType === MarketType.Handicap);
}

export function isFullTimeMarketType(marketType: MarketType) {
    const marketTypeName = keyLookup(MarketType, marketType);
    if (marketTypeName === undefined) {
        return null;
    }
    return !marketTypeName.startsWith('FH_');
}

export function getMainMarketTypes(sportType: number) {
    let thirdMarketType;
    switch (sportType) {
        case SportType.Soccer:
        case SportType.Cricket:
        case SportType.Futsal:
        case SportType.GaelicFootball:
        case SportType.Hurling:
        case SportType.Bandy:
        case SportType.Others:
        case SportType.ESports:
            thirdMarketType = [MarketType._1X2, MarketType.FH_1X2];
            break;
        case SportType.Badminton:
        case SportType.MotorSport:
        case SportType.Handball:
        case SportType.BeachSoccer:
        case SportType.GeneralHockey:
        case SportType.AussieRulesFootball:
            thirdMarketType = [MarketType.OddEven, MarketType.FH_OddEven];
            break;
        default:
            thirdMarketType = [MarketType.MoneyLine, MarketType.FH_OddEven];
    }

    return [
        MarketType.Handicap, MarketType.FH_Handicap,
        MarketType.OverUnder, MarketType.FH_OverUnder,
    ].concat(thirdMarketType);
}

export function isInvalidLiveMp1X2Odds(odds: IOdds, isMixParlayMode: boolean) {
    return isMixParlayMode
        && odds.isLive
        && (odds.marketType === MarketType._1X2 || odds.marketType === MarketType.FH_1X2)
        && odds.eventResult.liveHomeScore - odds.eventResult.liveAwayScore !== 0
        && odds.priceOption.some(x => x.price < 1.5);
}

export function getCategory(marketType: number) {
    const marketTypeKey = keyLookup(MarketType, Number(marketType));
    const FHRegex = /(FH_(?!\d)|FH)/;
    return FHRegex.test(marketTypeKey) ? 'firstHalf' : 'fullTime';
}

export function getMarketTypeName(marketType: number) {
    const marketTypeKey = keyLookup(MarketType, Number(marketType));
    const FHRegex = /(FH_(?!\d)|FH)/;
    return marketTypeKey.replace(FHRegex, '');
}

/*
 *  wrapRows = true, when odds exceed expectedOddsLen, it next odds will go into another array. each array will have expectedOddsLen
 *  example: getMainMarketOdds([a, b, c], soccer, 2, true) => [[a, b],[c, null]]
 *
 *  wrapRows = false, will only ensure it will have at least the expectedOddsLen
 *  example: getMainMarketOdds([a, b, c], soccer, 2, false) => [[a, b, c]]
 *  example: getMainMarketOdds([a], soccer, 2, false) => [[a, null]]
*/
export function getMainMarketOdds(odds: IOdds[], sportType: number, expectedOddsLen: number, wrapRows: boolean): MainMarketOddsResult {
    if (!odds) {
        return {};
    }

    const marketTypeList = getMainMarketTypes(sportType);
    const isAllowedMarketType = (marketType: number) => marketTypeList.includes(marketType);

    const filteredOdds = odds
        .filter(x => x.marketGroup.id === 0)
        // filter out non-allowed marketType
        .filter(({ marketType }) => isAllowedMarketType(marketType));

    const groupByCategory: MainMarketOddsResult = {};
    const categoryList = ['fullTime', 'firstHalf'];
    categoryList.forEach((category) => {
        const categoryMarketType = filteredOdds
            .map(x => x.marketType)
            .filter(marketType => getCategory(marketType) === category);

        // special case: at least have empty fullTime category if no odds
        if (categoryMarketType.length || category === 'fullTime') {
            const marketTypes = marketTypeList.filter(marketType => getCategory(marketType) === category);
            const groupByMarketType = marketTypes.reduce<Record<string, {type: 'MainMarketOddsResult', data: IOdds[][]}>>((acc, marketType) => {
                const expectedLen = marketType === getLastElement(marketTypes) ? 1 : expectedOddsLen;
                const oddsList = filteredOdds
                    .filter(item => item.marketType === marketType)
                    .sort((a, b) => {
                        if (b.sortOrder === a.sortOrder) {
                            return a.point - b.point;
                        }
                        return b.sortOrder - a.sortOrder;
                    });

                const rowGroups = oddsList.reduce<IOdds[][]>((acc2, curr) => {
                    if (getLastElement(acc2).length === expectedLen && wrapRows) {
                        acc2.push([]);
                    }
                    getLastElement(acc2).push(curr);
                    return acc2;
                }, [[]]);

                rowGroups[rowGroups.length - 1] = padRight(getLastElement(rowGroups), expectedLen);

                const marketTypeName = getMarketTypeName(marketType);
                acc[marketTypeName] = {
                    type: 'MainMarketOddsResult',
                    data: rowGroups,
                };
                return acc;
            }, {});

            groupByCategory[category] = groupByMarketType;
        }
    });

    return groupByCategory;
}

export function getMainMarketOddsListBySportType(oddsList: IOdds[], sportType: SportType, isLive: boolean): MainMarketOdds[][] {
    return getMainMarketOddsList(oddsList, getMainMarketConfig(sportType, isLive));
}

export function getMainMarketOddsList(oddsList: IOdds[], config: MainMarketConfig[][]): MainMarketOdds[][] {
    return config.map((row) => {
        const displayRow = row.map((rule) => {
            const mainMarketOdds = oddsList.filter(odds => rule.betTypeGroups.includes(odds.marketGroup.id) && rule.marketType === odds.marketType);

            return {
                betTypeGroups: rule.betTypeGroups,
                marketType: rule.marketType,
                rawOddsList: mainMarketOdds,
                oddsList: _2dArrayPadRight(mainMarketOdds, NUMBER_OF_MAX_DISPLAYED_ODDS_BEFORE_EXPAND, true),
            };
        });

        const needPadCount = NUMBER_OF_BET_TYPE_PER_ROW - displayRow.length;

        return displayRow.concat(Array(needPadCount).fill({
            betTypeGroups: [BetTypeGroup.Unknown],
            marketType: MarketType.Unknown,
            rawOddsList: [],
            oddsList: _2dArrayPadRight([], NUMBER_OF_MAX_DISPLAYED_ODDS_BEFORE_EXPAND, true),
        }));
    });
}

export function filterCorrectScoreHomeOdds(oddsData: IOdds) {
    const clone = JSON.parse(JSON.stringify(oddsData));
    clone.priceOption = clone.priceOption.filter((x: IPriceOption) => {
        if (x.optionName === 'odds_option_cs_others') {
            return false;
        }
        const score = x.optionName.split(';');
        const home = Number(score[1]);
        const away = Number(score[2]);
        return home > away;
    });
    return clone;
}

export function filterCorrectScoreDrawOdds(oddsData: IOdds) {
    const clone = JSON.parse(JSON.stringify(oddsData));
    clone.priceOption = clone.priceOption.filter((x: IPriceOption) => {
        if (x.optionName === 'odds_option_cs_others') {
            return true;
        }
        const score = x.optionName.split(';');
        const home = Number(score[1]);
        const away = Number(score[2]);
        return home === away;
    });
    return clone;
}

export function filterCorrectScoreAwayOdds(oddsData: IOdds) {
    const clone = JSON.parse(JSON.stringify(oddsData));
    clone.priceOption = clone.priceOption.filter((x: IPriceOption) => {
        if (x.optionName === 'odds_option_cs_others') {
            return false;
        }
        const score = x.optionName.split(';');
        const home = Number(score[1]);
        const away = Number(score[2]);
        return home < away;
    });
    return clone;
}

export function filterFglgOdds(oddsData: IOdds, endsWith: string) {
    const clone = JSON.parse(JSON.stringify(oddsData));
    clone.priceOption = clone.priceOption.filter((x: IPriceOption) => x.option.endsWith(endsWith));
    return clone;
}

export function filterHtftOdds(oddsData: IOdds, startsWith: string) {
    const clone = JSON.parse(JSON.stringify(oddsData));
    clone.priceOption = clone.priceOption.filter((x: IPriceOption) => x.option.startsWith(startsWith));
    return clone;
}

export function getOtherMarketOdds(odds: IOdds[], sportType: number): OtherMarketOddsResult {
    if (!odds) {
        return {};
    }

    const groupByMarketType = odds
        .filter(x => x.marketGroup.id === 0)
        .filter(x => !getMainMarketTypes(sportType).includes(x.marketType))
        .sort((a, b) => b.sortOrder - a.sortOrder)
        .reduce<Record<string, IOdds>>((acc, curr) => {
        acc[curr.marketType] = curr;
        return acc;
    }, {});

    const groupByCategory = Object.keys(groupByMarketType).map(x => Number(x)).reduce<Record<string, Record<string, {type: 'OtherMarketOddsResult', data: OddsColumn[]}>>>((acc, marketType) => {
        const marketTypeName = keyLookup(MarketType, marketType).replace(/((FH|SH)_(?!\d)|FH|SH)/, '');

        let categoryName;
        switch (marketType) {
            case MarketType.Handicap:
            case MarketType.OverUnder:
            case MarketType.OddEven:
            case MarketType._1X2:
            case MarketType.LiveScore:
            case MarketType.CorrectScore:
                categoryName = 'fullTime';
                break;
            case MarketType.FH_Handicap:
            case MarketType.FH_OverUnder:
            case MarketType.FH_OddEven:
            case MarketType.FH_1X2:
            case MarketType.FH_LiveScore:
            case MarketType.FH_CorrectScore:
                categoryName = 'firstHalf';
                break;
            case MarketType.SH_CorrectScore:
                categoryName = 'secondHalf';
                break;
            default:
                categoryName = '';
        }

        let columns;
        switch (marketType) {
            case MarketType.CorrectScore:
            case MarketType.FH_CorrectScore:
            case MarketType.SH_CorrectScore:
                columns = [{
                    columnName: 'home',
                    oddsData: filterCorrectScoreHomeOdds(groupByMarketType[marketType]),
                }, {
                    columnName: 'draw',
                    oddsData: filterCorrectScoreDrawOdds(groupByMarketType[marketType]),
                }, {
                    columnName: 'away',
                    oddsData: filterCorrectScoreAwayOdds(groupByMarketType[marketType]),
                }];
                break;
            case MarketType.FGLG:
                columns = [{
                    columnName: 'firstGoal',
                    oddsData: filterFglgOdds(groupByMarketType[marketType], 'F'),
                }, {
                    columnName: 'lastGoal',
                    oddsData: filterFglgOdds(groupByMarketType[marketType], 'L'),
                }, {
                    columnName: 'noGoal',
                    oddsData: filterFglgOdds(groupByMarketType[marketType], 'NG'),
                }];
                break;
            case MarketType.HTFT:
                columns = [{
                    columnName: null,
                    oddsData: filterHtftOdds(groupByMarketType[marketType], 'H'),
                }, {
                    columnName: null,
                    oddsData: filterHtftOdds(groupByMarketType[marketType], 'D'),
                }, {
                    columnName: null,
                    oddsData: filterHtftOdds(groupByMarketType[marketType], 'A'),
                }];
                break;
            default:
                columns = [{
                    columnName: null,
                    oddsData: groupByMarketType[marketType],
                }];
        }

        acc[marketTypeName] = acc[marketTypeName] || {};
        acc[marketTypeName][categoryName] = {
            type: 'OtherMarketOddsResult',
            data: columns,
        };
        return acc;
    }, {});

    return groupByCategory;
}

export function isOneMinFastMarket(betTypeGroupId: number) {
    return betTypeGroupId >= BetTypeGroup.Soccer_FirstOneMinuteFastMarket
        && betTypeGroupId <= BetTypeGroup.Soccer_LastOneMinuteFastMarket;
}

export function isFiveMinFastMarket(betTypeGroupId: number) {
    return betTypeGroupId >= BetTypeGroup.Soccer_FirstFiveMinuteFastMarket
        && betTypeGroupId <= BetTypeGroup.Soccer_LastFiveMinuteFastMarket;
}

export function isFastMarket(betTypeGroupId: number) {
    return isOneMinFastMarket(betTypeGroupId)
        || isFiveMinFastMarket(betTypeGroupId);
}

export function isMarketInConfig(mainMarket: MainMarketConfig[][], betTypeGroup: BetTypeGroup, marketType: MarketType) {
    return mainMarket.some(group => group.some(
        config => config.betTypeGroups.includes(betTypeGroup)
            && config.marketType === marketType,
    ));
}

export function isMainMarket(sportType: SportType, isLive: boolean, betTypeGroup: BetTypeGroup, marketType: MarketType) {
    const mainMarket = getMainMarketConfig(sportType, isLive);
    return isMarketInConfig(mainMarket, betTypeGroup, marketType);
}

export function isTopTwoMainMarket(sportType: SportType, isLive: boolean, betTypeGroup: BetTypeGroup, marketType: MarketType) {
    const mainMarket = getMainMarketConfig(sportType, isLive);
    const topTwoMainMarket = mainMarket.slice(0, TOP_TWO_ROW);
    return isMarketInConfig(topTwoMainMarket, betTypeGroup, marketType);
}

export function getGroupName(marketGroup: IMarketGroup) {
    if (isOneMinFastMarket(marketGroup.id)) {
        return i18n.t('one_Minute_Fast_Market');
    }

    if (isFiveMinFastMarket(marketGroup.id)) {
        return i18n.t('five_Minutes_Fast_Market');
    }

    return getTranslated(marketGroup.name);
}

export const marketTypeDisplayOrder = [
    MarketType.Handicap,
    MarketType.FH_Handicap,
    MarketType.OverUnder,
    MarketType.FH_OverUnder,
    MarketType.OddEven,
    MarketType.FH_OddEven,
    MarketType._1X2,
    MarketType.FH_1X2,
    MarketType.MoneyLine,
    MarketType.CorrectScore,
    MarketType.FH_CorrectScore,
    MarketType.SH_CorrectScore,
    MarketType.TotalGoal,
    MarketType.HTFT,
    MarketType.FGLG,
    MarketType.DoubleChance,
    MarketType.LiveScore,
    MarketType.FH_LiveScore,
];

export function getNewBetTypeOdds(odds: IOdds[]): NewBetTypeOddsResult {
    const data = odds
        .filter(x => x.marketGroup.id !== 0)
        .sort((a, b) => {
            if (a.marketGroup.displayPriority === b.marketGroup.displayPriority) {
                if (marketTypeDisplayOrder.indexOf(a.marketType) === marketTypeDisplayOrder.indexOf(b.marketType)) {
                    return b.sortOrder - a.sortOrder;
                }

                return marketTypeDisplayOrder.indexOf(a.marketType) - marketTypeDisplayOrder.indexOf(b.marketType);
            }
            return a.marketGroup.displayPriority - b.marketGroup.displayPriority;
        })
        .reduce<Record<string, Record<string, {type: 'NewBetTypeOddsResult', data: NewBetTypeColumn[]}>>>((acc, curr) => {
        const groupName = getGroupName(curr.marketGroup);
        const marketType = keyLookup(MarketType, curr.marketType);

        acc[groupName] = acc[groupName] || {};
        acc[groupName][marketType] = acc[groupName][marketType] || {
            type: 'NewBetTypeOddsResult',
            data: [],
        };
        acc[groupName][marketType].data.push({
            columnName: null,
            oddsData: curr,
            marketTypeDisplayOrder: marketTypeDisplayOrder.indexOf(curr.marketType),
        });
        return acc;
    }, {});

    return data;
}

export function getMatchDetailOdds(odds: IOdds[], sportType: number): MatchDetailOddsResult {
    if (!odds) {
        return {
            mainMarket: {},
            otherMarket: {},
            newBetType: {},
        };
    }

    return {
        mainMarket: getMainMarketOdds(odds, sportType, 4, false),
        otherMarket: getOtherMarketOdds(odds, sportType),
        newBetType: getNewBetTypeOdds(odds),
    };
}

export function isShowPointMarket(marketType: number) {
    const ShowPointMarket = [
        MarketType.Handicap,
        MarketType.OverUnder,
        MarketType.FH_Handicap,
        MarketType.FH_OverUnder,
    ];
    return ShowPointMarket.includes(marketType);
}

export function isShowPointMarketForIndia(marketType: number) {
    const ShowPointMarket = [
        MarketType.Handicap,
        MarketType.FH_Handicap,
    ];
    return ShowPointMarket.includes(marketType);
}

export function isShowPointGroup(marketGroupId: number) {
    const NotShowPointGroup = [
        BetTypeGroup.Basketball_FirstBasket,
        BetTypeGroup.Basketball_LastBasket,
        BetTypeGroup.Basketball_RaceToFirst20Point,
        BetTypeGroup.Basketball_TeamWithHighestScoringQuarter,
    ];
    return !NotShowPointGroup.includes(marketGroupId);
}

export function isShowPointSport(sportType: SportType) {
    const NotShowPointSport = [
        SportType.MuayThai,
    ];
    return !NotShowPointSport.includes(sportType);
}

export function isValidPrice(odds: IOdds) {
    const UnSupportedSingleSideMarkets = [
        MarketType.Handicap,
        MarketType.FH_Handicap,
        MarketType.OverUnder,
        MarketType.FH_OverUnder,
        MarketType.OddEven,
        MarketType.FH_OddEven,
        MarketType._1X2,
        MarketType.FH_1X2,
        MarketType.LiveScore,
        MarketType.FH_LiveScore,
        MarketType.MoneyLine,
    ];

    if (UnSupportedSingleSideMarkets.includes(odds.marketType)) {
        return odds.priceOption.every(priceOption => priceOption.price !== 0);
    }

    return true;
}

export function calculateMoreMarketCount(sportType: SportType, isLive: boolean, marketTypeRawData: string[]) {
    const topTwoMainMarket = getMainMarketConfig(sportType, isLive).slice(0, TOP_TWO_ROW);
    return calculateMoreMarketCountByConfig(topTwoMainMarket, marketTypeRawData);
}

export function calculateMoreMarketCountByConfig(mainMarket: MainMarketConfig[][], marketTypeRawData: string[]) {
    if (!marketTypeRawData || !Array.isArray(marketTypeRawData)) {
        return 0;
    }

    // convert "0:2:3" to [0,2,3]
    const marketTypeData = marketTypeRawData
        .map(item => item.split(':').map(x => Number(x)));

    const marketCount = marketTypeData
        .reduce(
            (count, marketData) => {
                const [betTypeGroupId, marketType, oddsCount] = marketData;

                if (isOneMinFastMarket(betTypeGroupId)) {
                    count.has1minFastMarket = true;
                    return count;
                }

                if (isFiveMinFastMarket(betTypeGroupId)) {
                    count.has5minFastMarket = true;
                    return count;
                }

                const _isMainMarket = isMarketInConfig(mainMarket, betTypeGroupId, marketType);

                if ((_isMainMarket && oddsCount > NUMBER_OF_MAX_DISPLAYED_ODDS_BEFORE_EXPAND) || (!_isMainMarket && oddsCount > 0)) {
                    count.total += 1;
                }
                return count;
            },
            {
                total: 0,
                has1minFastMarket: false,
                has5minFastMarket: false,
            },
        );
    return marketCount.total + (marketCount.has1minFastMarket ? 1 : 0) + (marketCount.has5minFastMarket ? 1 : 0);
}

export function sortOdds<T extends IOdds >(odds: T[]): T[] {
    return orderBy(odds, [x => x.sortOrder, x => x.point], ['desc', 'asc']);
}

export function sortMatches<T extends IMatch>(matches: T[]): T[] {
    return orderBy(matches, x => x.eventCode);
}

export function sortOutrightTournaments(tournaments: IOutrightTournament[]) {
    return orderBy(tournaments, [x => x.leagueCode, x => x.isLive], ['asc', 'desc']);
}

export function isDynamicMarket(marketType: MarketType) {
    return marketType === MarketType.Dynamic;
}

export function isCorrectScoreMarket(marketType: MarketType) {
    return [MarketType.CorrectScore, MarketType.FH_CorrectScore, MarketType.SH_CorrectScore].includes(marketType);
}
